import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Fragment, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEvent } from '../hooks/useEvents'
import { formatEventDates } from '../lib/dates'
import { ApiClient } from '../api'
import { EditEventModal } from '../components/EditEventModal'
import { ActivitiesTab } from '../components/ActivitiesTab'
import { ComponentsTab } from '../components/ComponentsTab'
import { ScheduleTab } from '../components/ScheduleTab'
import { ScheduleTabV2 } from '../components/event-v2/ScheduleTabV2'
import { ServiceTimetableTab } from '../components/ServiceTimetableTab'
import { RiskRegisterTabV2 } from '../components/event-v2/RiskRegisterTabV2'
import { isEnabled } from '../lib/featureFlags'
import { RosterTab } from '../components/RosterTab'
import { ChecklistsTab } from '../components/ChecklistsTab'
import { RisksTab } from '../components/RisksTab'
import { NotesTab } from '../components/NotesTab'
import { BudgetTab } from '../components/BudgetTab'
import { FinancialsTab } from '../components/FinancialsTab'
import { EventForecastTab } from '../components/forecast/EventForecastTab'
import { TicketingTab } from '../components/TicketingTab'
import { CateringTab } from '../components/CateringTab'
import { ProductBriefingTab } from '../components/ProductBriefingTab'
import { VolunteersPanel } from './Volunteers'
import { BriefingsTab } from '../components/BriefingsTab'
import { ActionsTab } from '../components/ActionsTab'
import { OverviewTab } from '../components/OverviewTab'
import { PlanTab } from '../components/PlanTab'
import { StationMapTab } from '../components/StationMapTab'
import { LiveTab } from '../components/LiveTab'
import { ResourcesPanel } from '../components/ResourcesPanel'
import { useMe } from '../hooks/useMe'
import { ArchiveProvider } from '../contexts/ArchiveContext'
import { Icon } from '../components/Icon'
import { EvIcon } from '../components/volunteer/EvIcon'
import { useRoster } from '../hooks/useRoster'
import { EVENT_RESOURCE_CATEGORIES } from '../types'
import type { EventListItem, EventStatus, EventResourceCategory } from '../types'

// EventDetail - per-event home. Header (name + status + Edit) and meta row
// (type chip + dates + location) sit at page level; the tab strip below
// switches between Overview, Activities, Components, and Schedule.
//
// More tabs land here as Phase 2 ships - Roster, Checklists, etc.
// Each is its own component so this file stays a small switchboard and
// individual tab files can grow without bloating the page.
//
// Tab state is local React state - we don't put it in the URL yet because
// none of the tabs need to be deep-linkable (a manager either lands on the
// event and uses tabs, or follows a notification link that already targets
// the right view). Easy to graft on `?tab=schedule` later if it matters.

type TabKey = 'overview' | 'activities' | 'actions' | 'plan' | 'components' | 'checklists' | 'risks' | 'schedule' | 'service-timetable' | 'map' | 'roster' | 'briefings' | 'budget' | 'financials' | 'forecast' | 'ticketing' | 'catering' | 'volunteers' | 'product-briefing' | 'notes' | 'live'
// Tabs are split into two phases of an event's life: "Planning" (the work
// before the day) and "Running" (the day itself). Overview sits above both
// as the landing summary. The group label is rendered as a non-clickable
// chip before the first tab of each group in the strip below.
type TabGroup = 'plan' | 'run'
interface TabSpec {
  key: TabKey
  label: string
  group?: TabGroup
  financialOnly?: boolean
  adminOnly?: boolean
}
const GROUP_LABELS: Record<TabGroup, string> = {
  plan: 'Planning',
  run: 'Running'
}
// Colour-code the two phases so the strip reads at a glance: Planning = dark
// blue, Running = dark green. Overview (no group) stays a neutral slate.
const GROUP_COLORS: Record<TabGroup, string> = {
  plan: '#1e3a8a', // dark blue
  run:  '#0a4a3a'  // dark green (matches the volunteer-call button + live pill)
}
const NEUTRAL_TAB = '#57534e' // ungrouped (Overview)
function tabColor(group?: TabGroup): string {
  return group ? GROUP_COLORS[group] : NEUTRAL_TAB
}
const TABS: ReadonlyArray<TabSpec> = [
  { key: 'overview',   label: 'Overview' },
  // Planning the event. Project Plan leads (it's the spine of the prep), with
  // Actions immediately after so a task and its plan item sit side by side.
  { key: 'plan',       label: 'Project Plan', group: 'plan' },
  { key: 'actions',    label: 'Actions',      group: 'plan' },
  { key: 'activities', label: 'Activities',   group: 'plan' },
  { key: 'components', label: 'Approvals',    group: 'plan' },
  { key: 'checklists', label: 'Checklists',   group: 'plan' },
  { key: 'risks',      label: 'Risks',        group: 'plan' },
  { key: 'budget',     label: 'Budget',       group: 'plan', financialOnly: true },
  { key: 'financials', label: 'Financials',   group: 'plan', financialOnly: true },
  // Forecast Studio mirror — forecast vs plan for a committed forecast (EM-9).
  { key: 'forecast',   label: 'Forecast',     group: 'plan', financialOnly: true },
  { key: 'ticketing',  label: 'Ticketing',    group: 'plan' },
  { key: 'service-timetable', label: 'Service Timetable', group: 'plan' },
  { key: 'catering',   label: 'Catering',     group: 'plan' },
  { key: 'volunteers', label: 'Volunteers',   group: 'plan', adminOnly: true },
  { key: 'product-briefing', label: 'Product Briefing', group: 'plan' },
  { key: 'notes',      label: 'Notes',        group: 'plan' },
  // Running the event on the day.
  { key: 'schedule',   label: 'Schedule',     group: 'run' },
  { key: 'map',        label: 'Map',          group: 'run' },
  { key: 'roster',     label: 'Roster',       group: 'run' },
  { key: 'briefings',  label: 'Briefings',    group: 'run' },
  { key: 'live',       label: 'Live',         group: 'run' }
]

export function EventDetail() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const { data: event, isLoading, error } = useEvent(id)
  const { data: me } = useMe()
  // Live "N signed on now" count for the header pill (shares the roster cache).
  const { data: rosterData } = useRoster(id)
  const signedOnCount = rosterData?.signedOnUserIds.length ?? 0
  const [editing, setEditing] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [showResources, setShowResources] = useState(false)
  const [resourcesInitialCategory, setResourcesInitialCategory] = useState<EventResourceCategory | undefined>(undefined)
  const [tab, setTab] = useState<TabKey>('overview')

  // `?openResources=<category>` deep-link from /resources (spec 46).
  // On mount with a valid slug: pre-select that tab, open the panel,
  // and strip the param from the URL (replace state — no history
  // entry, so Back navigates to /resources rather than re-firing the
  // open).
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const raw = searchParams.get('openResources')
    if (!raw) return
    if ((EVENT_RESOURCE_CATEGORIES as readonly string[]).includes(raw)) {
      setResourcesInitialCategory(raw as EventResourceCategory)
      setShowResources(true)
    }
    const next = new URLSearchParams(searchParams)
    next.delete('openResources')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // `?tab=roster` deep-link (e.g. from the Volunteers section "Review & place").
  useEffect(() => {
    const t = searchParams.get('tab')
    if (t && TABS.some((x) => x.key === t)) setTab(t as TabKey)
  }, [searchParams])

  const visibleTabs = TABS.filter((t) =>
    (!t.financialOnly || me?.hasFinancial) &&
    (!t.adminOnly || me?.isAdmin)
  )

  // Bad URL param (non-numeric, missing) - bail out before render
  if (!Number.isInteger(id) || id <= 0) {
    return <NotFound />
  }

  if (isLoading) {
    return (
      <div className="space-y-5 max-w-4xl">
        <BackLink />
        <div className="text-ink-soft text-sm font-mono">Loading event...</div>
      </div>
    )
  }

  // The api wrapper throws Error('${status} ${statusText}: ...') on non-2xx.
  // 404 -> show NotFound; everything else -> show the raw error so we can debug.
  if (error) {
    if (error instanceof Error && error.message.startsWith('404')) {
      return <NotFound />
    }
    return (
      <div className="space-y-5 max-w-4xl">
        <BackLink />
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load event</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      </div>
    )
  }

  if (!event) return <NotFound />

  const archived = event.archivedAt != null

  return (
    <ArchiveProvider archived={archived}>
    {/* The reading column (max-w-4xl) suits every tab but Ticketing, whose
        three-column Carriages card needs the full width (Chris, 2026-09-18). */}
    <div className={tab === 'ticketing' ? 'space-y-6' : 'space-y-6 max-w-4xl'}>
      <BackLink />

      {/* Header: name + status + edit */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-3xl leading-tight flex-1">{event.name}</h1>
        <div className="flex items-center gap-2 flex-shrink-0 pt-1">
          <StatusBadge status={event.status} archivedAt={event.archivedAt} />
          {me?.isAdmin && !archived && (
            <button
              type="button"
              onClick={() => setTab('volunteers')}
              className="px-3 py-1 text-sm font-semibold rounded inline-flex items-center gap-1.5 text-white"
              style={{ background: '#0a4a3a' }}
              title="Send a call for volunteers and manage this event's volunteers"
            >
              <EvIcon name="send" size={14} color="#fff" /> Send call for volunteers
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowResources(true)}
            className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5"
            title="Quick reference — briefing pack, risk register, station map, and other shortcuts."
          >
            <Icon name="book-open" size={14} />
            Resources
          </button>
          <a
            href={`/events/${event.id}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            title="Full internal Production Plan — opens a print preview; use the browser's Save as PDF."
          >
            Production Plan
          </a>
          <a
            href={`/events/${event.id}/event-plan`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            title="Public Event Plan summary (no risks, tasks or financials) — opens a print preview."
          >
            Event Plan
          </a>
          <a
            href={`/events/${event.id}/product-briefing`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            title="Complete product briefing for ticketing & marketing — opens a print preview; use the browser's Save as PDF."
          >
            Product Briefing
          </a>
          {!archived && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            >
              Edit
            </button>
          )}
          {me?.isAdmin && !archived && (
            <button
              type="button"
              onClick={() => setArchiving(true)}
              className="px-3 py-1 text-sm font-semibold rounded border border-line text-ink-soft hover:text-oxblood hover:border-oxblood transition-colors"
              title="Archive this event. Closes any active actions and plan tasks."
            >
              Archive
            </button>
          )}
        </div>
      </div>

      {archived && <ArchivedBanner event={event} />}

      {/* Meta row: type chip + dates + location */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="px-2 py-0.5 rounded text-xs font-semibold"
          style={{
            background: event.type.colourBg,
            color: event.type.colourText
          }}
        >
          {event.type.name}
        </span>
        <span className="text-ink-soft font-mono text-xs">
          {formatEventDates(event.dates, event.startDate, event.endDate)}
        </span>
        {event.location && (
          <span className="text-ink-soft text-xs">{event.location}</span>
        )}
        {signedOnCount > 0 && (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
            style={{ background: '#dcebe5', border: '1px solid #9fcbb6' }}
          >
            <span className="nymr-live-pulse-dot" style={{ background: '#137a52' }} />
            <span className="text-xs font-bold" style={{ color: '#0a4a3a' }}>
              {signedOnCount} signed on now
            </span>
          </span>
        )}
      </div>

      {/* Tab strip - underline-on-active pattern, mirrors EventsList status filter.
          Tabs wrap on narrow viewports; a "Planning" / "Running" chip is
          inserted before the first tab of each group (items 9, 11, 12). */}
      <div className="border-b border-line">
        <nav className="flex flex-wrap items-center gap-x-1 -mb-px" role="tablist" aria-label="Event sections">
          {visibleTabs.map((t, i) => {
            const active = tab === t.key
            const prev = visibleTabs[i - 1]
            const showGroupLabel = t.group != null && t.group !== prev?.group
            return (
              <Fragment key={t.key}>
                {showGroupLabel && (
                  <span
                    aria-hidden
                    className="select-none ml-2 mr-1.5 px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider text-white"
                    style={{ background: GROUP_COLORS[t.group!] }}
                  >
                    {GROUP_LABELS[t.group!]}
                  </span>
                )}
                <button
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => setTab(t.key)}
                  className={
                    'px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px ' +
                    (active ? 'font-bold' : 'font-semibold')
                  }
                  style={{
                    color: active ? tabColor(t.group) : tabColor(t.group) + 'b3',
                    borderBottomColor: active ? tabColor(t.group) : 'transparent',
                    background: active ? tabColor(t.group) + '14' : 'transparent'
                  }}
                >
                  {t.label}
                </button>
              </Fragment>
            )
          })}
        </nav>
      </div>

      {/* Tab body */}
      {tab === 'overview' && <OverviewTab event={event} />}
      {tab === 'activities' && <ActivitiesTab eventId={event.id} />}
      {tab === 'actions' && <ActionsTab eventId={event.id} />}
      {tab === 'plan' && <PlanTab eventId={event.id} eventStartDate={event.startDate} />}
      {tab === 'components' && <ComponentsTab eventId={event.id} />}
      {tab === 'checklists' && <ChecklistsTab eventId={event.id} eventStartDate={event.startDate} />}
      {tab === 'risks' && (
        isEnabled('risks')
          ? <RiskRegisterTabV2 event={event} />
          : <RisksTab eventId={event.id} />
      )}
      {tab === 'schedule' && (
        isEnabled('schedule')
          ? <ScheduleTabV2 event={event} />
          : <ScheduleTab event={event} />
      )}
      {tab === 'service-timetable' && <ServiceTimetableTab eventId={event.id} />}
      {tab === 'map' && <StationMapTab eventId={event.id} />}
      {tab === 'roster' && <RosterTab event={event} />}
      {tab === 'briefings' && <BriefingsTab eventId={event.id} />}
      {tab === 'budget' && <BudgetTab eventId={event.id} />}
      {tab === 'financials' && <FinancialsTab eventId={event.id} />}
      {tab === 'forecast' && <EventForecastTab eventId={event.id} />}
      {tab === 'ticketing' && <TicketingTab eventId={event.id} />}
      {tab === 'catering' && <CateringTab eventId={event.id} />}
      {tab === 'volunteers' && me?.isAdmin && !archived && <VolunteersPanel event={event} />}
      {tab === 'product-briefing' && <ProductBriefingTab eventId={event.id} />}
      {tab === 'notes' && <NotesTab eventId={event.id} />}
      {tab === 'live' && <LiveTab eventId={event.id} />}

      {editing && !archived && (
        <EditEventModal key={event.id} event={event} onClose={() => setEditing(false)} />
      )}
      {archiving && (
        <ArchiveConfirmModal
          eventId={event.id}
          eventName={event.name}
          onClose={() => setArchiving(false)}
        />
      )}
      {showResources && (
        <ResourcesPanel
          eventId={event.id}
          eventName={event.name}
          initialCategory={resourcesInitialCategory}
          onClose={() => {
            setShowResources(false)
            setResourcesInitialCategory(undefined)
          }}
        />
      )}
    </div>
    </ArchiveProvider>
  )
}

// ---- archived banner ---------------------------------------------------
//
// Sits between the meta row and the tab strip when archivedAt is set.
// Surfaces the audit info (when, by whom) and the global read-only
// notice so the user can't miss the state.

function ArchivedBanner({ event }: { event: EventListItem }) {
  const when = event.archivedAt
    ? new Date(event.archivedAt).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '—'
  return (
    <div
      className="bg-parchment border border-line rounded p-3 flex items-start gap-3 text-sm"
      role="status"
    >
      <span aria-hidden className="text-ink-soft pt-0.5">
        <Icon name="info" size={16} />
      </span>
      <div className="flex-1">
        <div className="font-semibold text-ink">This event is archived</div>
        <div className="text-xs text-ink-soft mt-0.5">
          Archived on <span className="font-mono">{when}</span>
          {event.archivedById != null && ' by an admin'}. Read-only — every
          tab below renders existing data without edit controls.
        </div>
      </div>
    </div>
  )
}

// ---- archive confirm modal ----------------------------------------------
//
// Tweak T2a. Fetches the preview counts before showing the body so the
// admin knows exactly what's about to be cascade-closed. Cancel is the
// default focus; the Archive button is brass-styled to flag the
// destructive-ish nature without being shouty.

function ArchiveConfirmModal({
  eventId, eventName, onClose
}: {
  eventId: number
  eventName: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['archive-preview', eventId],
    queryFn: () => ApiClient.archivePreview(eventId)
  })

  const mut = useMutation({
    mutationFn: () => ApiClient.archiveEvent(eventId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId] })
      qc.invalidateQueries({ queryKey: ['events'] })
      qc.invalidateQueries({ queryKey: ['archived-events'] })
      qc.invalidateQueries({ queryKey: ['actions', eventId] })
      qc.invalidateQueries({ queryKey: ['plan-tasks', eventId] })
      onClose()
    }
  })

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md my-16 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">Archive event</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="text-ink-soft hover:text-ink text-xl disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <p className="text-sm mb-3">
          Archive <span className="font-semibold">{eventName}</span>? This:
        </p>
        <ul className="text-sm text-ink-soft list-disc pl-5 space-y-1 mb-4">
          <li>Removes the event from default views (Events list, reports, My Actions).</li>
          <li>
            {previewLoading
              ? <span className="italic">Counting active items…</span>
              : (
                <>
                  Closes <span className="font-semibold text-ink">{preview?.activeActions ?? 0}</span>{' '}
                  active action{preview?.activeActions === 1 ? '' : 's'} and{' '}
                  <span className="font-semibold text-ink">{preview?.activePlanTasks ?? 0}</span>{' '}
                  plan task{preview?.activePlanTasks === 1 ? '' : 's'} (closed_reason: event-archived).
                </>
              )
            }
          </li>
          <li>Locks the event read-only — no further edits.</li>
        </ul>

        <p className="text-xs text-ink-soft italic mb-4">
          You can unarchive from the event header later. Closed tasks won't auto-reopen.
        </p>

        {mut.isError && (
          <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs mb-3">
            Couldn't archive: {(mut.error as Error).message}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            autoFocus
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="px-4 py-2 rounded text-sm font-semibold bg-brass text-white hover:bg-brass/90 disabled:opacity-50"
          >
            {mut.isPending ? 'Archiving…' : 'Archive event'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- helpers --------------------------------------------------------------

function BackLink() {
  return (
    <Link
      to="/events"
      className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-green transition-colors"
    >
      <span aria-hidden>←</span>
      <span>Back to Events</span>
    </Link>
  )
}

function NotFound() {
  return (
    <div className="space-y-5 max-w-4xl">
      <BackLink />
      <div className="bg-white border border-line rounded p-8 text-center">
        <h1 className="text-2xl mb-2">Event not found</h1>
        <p className="text-ink-soft text-sm">
          That event doesn’t exist or has been removed.
        </p>
      </div>
    </div>
  )
}

function StatusBadge({ status, archivedAt }: { status: EventStatus; archivedAt?: string | null }) {
  if (archivedAt) {
    return (
      <span className="px-2.5 py-1 text-sm font-semibold border rounded bg-ink-soft text-parchment border-ink-soft whitespace-nowrap">
        Archived
      </span>
    )
  }
  const styles: Record<EventStatus, string> = {
    'Idea':            'bg-brass-soft text-brass border-brass',
    'Draft':           'bg-parchment text-ink-soft border-line',
    'In Development':  'bg-brass-soft text-brass border-brass',
    'Live':            'bg-oxblood-soft text-oxblood border-oxblood',
    'Complete':        'bg-green-soft text-green border-green',
    'Cancelled':       'bg-parchment text-ink-soft border-line'
  }
  return (
    <span className={`px-2.5 py-1 text-sm font-semibold border rounded ${styles[status]} whitespace-nowrap`}>
      {status}
    </span>
  )
}
